import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/use-page-meta";
import { Check, CreditCard, DoorOpen, ClipboardList, MapPin, Users, Calendar, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PublicHeader } from "@/components/layout/PublicHeader";
import { PublicFooter } from "@/components/layout/PublicFooter";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { formatPlanPriceDisplay, PRICE_DISPLAY_CURRENCIES } from "@/lib/fx-rates";
import { detectPublicMarket, getStoredPricingCurrency, storePricingCurrency } from "@/lib/public-market";
import { isNativeIosApp } from "@/lib/platform";
import { getActivationFeeGhs } from "@/lib/signup-fee";

type Plan = {
  name: string;
  activationFeeGhs?: number;
  popular?: boolean;
  cta: { label: string; to: string; variant?: "default" | "outline" | "gradient" };
  highlights: Array<{ icon: any; text: string }>;
  footnote?: string;
};

const plans: Plan[] = [
  {
    name: "Starter",
    activationFeeGhs: getActivationFeeGhs("starter"),
    cta: { label: "Get Started", to: "/signup", variant: "gradient" },
    highlights: [
      { icon: CreditCard, text: "Includes 1 primary module" },
      { icon: Calendar, text: "Appointments: up to 500 bookings / month" },
      { icon: DoorOpen, text: "Memberships: up to 500 member visits / month" },
      { icon: ClipboardList, text: "Catering: up to 500 order submissions / month" },
      { icon: Users, text: "Up to 3 staff (calendars)" },
      { icon: MapPin, text: "1 business location" },
      { icon: Bell, text: "Email notifications + in‑app alerts" },
      { icon: Users, text: "Up to 1,000 customers (shared database)" },
    ],
    footnote: "7 days free to explore, then a one-time payment — no monthly subscription.",
  },
  {
    name: "Professional",
    activationFeeGhs: getActivationFeeGhs("professional"),
    popular: true,
    cta: { label: "Start Free Trial", to: "/signup", variant: "gradient" },
    highlights: [
      { icon: CreditCard, text: "Includes 1 primary module" },
      { icon: Calendar, text: "Appointments: up to 1,000 bookings / month" },
      { icon: DoorOpen, text: "Memberships: up to 1,000 member visits / month" },
      { icon: ClipboardList, text: "Catering: up to 1,000 order submissions / month" },
      { icon: Users, text: "Up to 10 staff (calendars)" },
      { icon: MapPin, text: "Up to 5 business locations" },
      { icon: Bell, text: "Email notifications + in‑app alerts" },
      { icon: Users, text: "Up to 5,000 customers (shared database)" },
    ],
    footnote: "7 days free to explore, then a one-time payment — no monthly subscription.",
  },
  {
    name: "Enterprise",
    cta: { label: "Contact Sales", to: "mailto:sales@buukd.app?subject=Enterprise%20Plan%20Inquiry", variant: "outline" },
    highlights: [
      { icon: CreditCard, text: "Choose primary module + enable more modules" },
      { icon: Calendar, text: "Custom usage limits (bookings/visits/submissions)" },
      { icon: Users, text: "Custom staff limits" },
      { icon: MapPin, text: "Custom multi-location" },
      { icon: Bell, text: "Dedicated onboarding & support" },
      { icon: Users, text: "Custom customer limits (shared database)" },
    ],
    footnote: "For high-volume businesses and teams that need custom requirements.",
  },
];

export default function Pricing() {
  const [displayCurrency, setDisplayCurrency] = useState<string>("GHS");
  const [autoDetectedCurrency, setAutoDetectedCurrency] = useState<string>("GHS");
  const publicMarket = useMemo(() => detectPublicMarket(), []);
  const isNativeIos = isNativeIosApp();

  usePageMeta({
    title: "Pricing – Buukd | Plans for Appointments, Memberships & Catering",
    description:
      "Buukd pricing: Starter and Professional one-time payment. 7 days free to explore. Appointments, memberships, catering. Enterprise custom.",
    path: "/pricing",
  });

  useEffect(() => {
    const storedCurrency = getStoredPricingCurrency();
    const detectedCurrency = storedCurrency || publicMarket.currency;
    setDisplayCurrency(detectedCurrency);
    setAutoDetectedCurrency(publicMarket.currency);
  }, [publicMarket.currency]);

  useEffect(() => {
    storePricingCurrency(displayCurrency);
  }, [displayCurrency]);

  if (isNativeIos) {
    return (
      <div className="min-h-screen flex flex-col">
        <PublicHeader />
        <main className="flex-1 pt-24 pb-16 lg:pt-28 lg:pb-24 bg-gradient-hero relative overflow-hidden">
          <div className="absolute inset-0 bg-dotted opacity-50" />
          <div className="container-app relative">
            <div className="mx-auto max-w-xl rounded-2xl border border-border bg-card p-6 text-center shadow-soft sm:p-8">
              <h1 className="text-3xl font-bold text-foreground">Organization access only</h1>
              <p className="mt-4 text-sm text-muted-foreground sm:text-base">
                Buukd subscriptions and organization onboarding are managed outside the iOS app.
                Authorized owners and staff can sign in to access their existing business workspace.
              </p>
              <Button asChild variant="gradient" className="mt-6">
                <Link to="/signin">Sign in</Link>
              </Button>
            </div>
          </div>
        </main>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1 pt-24 pb-16 lg:pt-28 lg:pb-24 bg-gradient-hero relative overflow-hidden">
        <div className="absolute inset-0 bg-dotted opacity-50" />
        <div className="container-app relative">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">
              Simple pricing for a modular platform
            </h1>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
              Choose one primary module at signup (Appointments, Memberships, or Catering Services). Start with 7 days free,
              then a one-time payment to get started — no monthly subscription.
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              Choose your currency below. Plan payments are one-time.
            </p>
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              {PRICE_DISPLAY_CURRENCIES.map((code) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => setDisplayCurrency(code)}
                  className={cn(
                    "px-3 py-1 text-xs rounded-full border transition-colors",
                    displayCurrency === code
                      ? "bg-pink-500/20 border-pink-500/50 text-pink-700 dark:text-pink-300"
                      : "border-border text-muted-foreground hover:text-foreground hover:border-border/80"
                  )}
                >
                  {code}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-10 lg:mt-14 grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-6 items-stretch">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={cn(
                  "bg-card rounded-2xl border border-border shadow-soft overflow-hidden",
                  plan.popular && "ring-2 ring-pink-500/40"
                )}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className={cn("text-sm font-semibold", plan.popular ? "text-pink-500" : "text-muted-foreground")}>
                        {plan.name}
                      </p>
                      {plan.popular && (
                        <Badge className="mt-2 bg-pink-500/10 text-pink-500 border-pink-500/20 hover:bg-pink-500/10">
                          Popular
                        </Badge>
                      )}
                    </div>
                  </div>

                  {typeof plan.activationFeeGhs === "number" ? (
                    <div className="mt-5 text-center">
                      <div className="flex items-end justify-center gap-2">
                        <span className="text-4xl font-bold text-foreground">
                          {formatPlanPriceDisplay(plan.activationFeeGhs, displayCurrency)}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">One-time payment</p>
                    </div>
                  ) : (
                    <div className="mt-5 text-center">
                      <span className="text-2xl font-bold text-foreground">Contact Sales</span>
                    </div>
                  )}

                  <div className="mt-6 space-y-3">
                    {plan.highlights.map((h, idx) => (
                      <div key={idx} className="flex items-start gap-3 text-sm">
                        <div className="mt-0.5 h-5 w-5 rounded-full bg-pink-500/10 flex items-center justify-center flex-shrink-0">
                          <Check className="h-3.5 w-3.5 text-pink-500" />
                        </div>
                        <span className="text-foreground">{h.text}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6">
                    {plan.cta.to.startsWith("mailto:") ? (
                      <Button asChild variant={plan.cta.variant ?? "default"} className="w-full">
                        <a href={plan.cta.to}>{plan.cta.label}</a>
                      </Button>
                    ) : (
                      <Button asChild variant={(plan.cta.variant as any) ?? "default"} className="w-full">
                        <Link to={plan.cta.to}>{plan.cta.label}</Link>
                      </Button>
                    )}
                    {plan.footnote && (
                      <p className="mt-3 text-xs text-muted-foreground text-center">
                        {plan.footnote}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <div className="bg-card rounded-2xl border border-border p-6 shadow-soft">
              <h3 className="font-semibold text-foreground">What’s included in every plan</h3>
              <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                <div className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-pink-500 mt-0.5" />
                  <span><span className="text-foreground font-medium">1 primary module</span> (Appointments, Memberships, or Catering Services)</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-pink-500 mt-0.5" />
                  <span>Shared customer database (one customer record across modules)</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-pink-500 mt-0.5" />
                  <span>In-app notifications + email notifications</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-pink-500 mt-0.5" />
                  <span>Analytics dashboards that adapt to your module</span>
                </div>
              </div>
              <p className="mt-4 text-xs text-muted-foreground">
                Need SMS reminders? SMS delivery costs vary by network — we can also run this as a separate “SMS wallet” so usage never eats your margin.
              </p>
            </div>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}


